function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatTimestamp(ts) {
  if (!ts) return "N/A";
  if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleString("en-IN");
  const parsed = Date.parse(String(ts));
  if (Number.isFinite(parsed)) return new Date(parsed).toLocaleString("en-IN");
  return "N/A";
}

function buildPrescriptionHtml({ prescription = {}, booking = {} }) {
  const createdAt = formatTimestamp(prescription.createdAt);
  const bookingDate = String(booking.bookingDate || "N/A");
  const petName = String(prescription.petName || booking?.petDetails?.name || "Pet");
  const providerName = String(prescription.providerDisplayName || booking?.assignedVetProfile?.displayName || "Verified Vet");
  const serviceTitle = String(prescription.serviceTitle || booking.serviceTitle || "Vet Consultation");
  const diagnosis = String(prescription.diagnosis || "-");
  const medicines = String(prescription.medicines || "-");
  const advice = String(prescription.advice || "-");
  const followUpDate = String(prescription.followUpDate || "-");
  const ownerEmail = String(booking.ownerEmail || booking.userEmail || "");
  const bookingId = String(booking.id || prescription.bookingId || "");

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Petaar Prescription</title>
  <style>
    body { font-family: Arial, sans-serif; color: #0f172a; margin: 0; background: #f8fafc; }
    .page { max-width: 840px; margin: 18px auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
    .header { background: linear-gradient(120deg, #1e40af, #2563eb); color: #fff; padding: 16px 20px; display: flex; align-items: center; gap: 12px; }
    .logo { width: 44px; height: 44px; border-radius: 10px; object-fit: cover; background: #fff; }
    .brand { font-size: 24px; font-weight: 700; line-height: 1.1; }
    .sub { font-size: 12px; opacity: 0.95; }
    .content { padding: 18px 20px 22px; }
    .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 18px; margin-bottom: 14px; }
    .label { font-size: 12px; color: #475569; text-transform: uppercase; letter-spacing: 0.3px; }
    .value { font-size: 15px; font-weight: 600; margin-top: 3px; }
    .section { border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; margin-top: 10px; }
    .section h3 { margin: 0 0 8px; font-size: 16px; color: #1e40af; }
    .text { white-space: pre-wrap; line-height: 1.5; }
    .footer { padding: 14px 20px 20px; color: #64748b; font-size: 12px; }
    .actions { display: flex; justify-content: flex-end; padding: 0 20px 16px; }
    .print-btn { border: 1px solid #1d4ed8; background: #1d4ed8; color: #fff; border-radius: 8px; font-weight: 700; padding: 8px 12px; cursor: pointer; }
    @media print {
      body { background: #fff; }
      .page { margin: 0; border: none; border-radius: 0; }
      .actions { display: none; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <img src="/petaar-logo.jpeg" alt="Petaar" class="logo" onerror="this.src='/logo192.png'" />
      <div>
        <div class="brand">Petaar Prescription</div>
        <div class="sub">Digitally issued through Petaar consultation platform</div>
      </div>
    </div>

    <div class="content">
      <div class="grid">
        <div>
          <div class="label">Booking ID</div>
          <div class="value">${escapeHtml(bookingId || "N/A")}</div>
        </div>
        <div>
          <div class="label">Issued On</div>
          <div class="value">${escapeHtml(createdAt)}</div>
        </div>
        <div>
          <div class="label">Pet Name</div>
          <div class="value">${escapeHtml(petName)}</div>
        </div>
        <div>
          <div class="label">Consultation Date</div>
          <div class="value">${escapeHtml(bookingDate)}</div>
        </div>
        <div>
          <div class="label">Vet</div>
          <div class="value">${escapeHtml(providerName)}</div>
        </div>
        <div>
          <div class="label">Service</div>
          <div class="value">${escapeHtml(serviceTitle)}</div>
        </div>
      </div>

      <div class="section">
        <h3>Diagnosis</h3>
        <div class="text">${escapeHtml(diagnosis)}</div>
      </div>
      <div class="section">
        <h3>Medicines & Dosage</h3>
        <div class="text">${escapeHtml(medicines)}</div>
      </div>
      <div class="section">
        <h3>Advice / Precautions</h3>
        <div class="text">${escapeHtml(advice)}</div>
      </div>
      <div class="section">
        <h3>Follow-up</h3>
        <div class="text">${escapeHtml(followUpDate)}</div>
      </div>
    </div>

    <div class="actions">
      <button class="print-btn" onclick="window.print()">Print / Save PDF</button>
    </div>

    <div class="footer">
      This prescription is generated on Petaar. For emergencies, contact nearest veterinary emergency service immediately.
      ${ownerEmail ? `<br />For account reference: ${escapeHtml(ownerEmail)}` : ""}
    </div>
  </div>
</body>
</html>`;
}

export function openBrandedPrescription({ prescription, booking }) {
  const html = buildPrescriptionHtml({ prescription, booking });
  const popup = window.open("", "_blank", "noopener,noreferrer,width=920,height=760");
  if (!popup) {
    alert("Popup blocked. Please allow popups to view prescription.");
    return;
  }
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
}
